import type { CatalogDownloadProgress, CatalogDownloadPort } from '../domain/ports/CatalogDownloadPort';
import type { StartupCatalogSyncOutcome } from './startupCatalogSyncOutcome.ts';
import {
  SYNC_CATALOG_STARTUP_DOWNLOAD_TIMEOUT_AFTER_REVISION_MS,
  SYNC_CATALOG_STARTUP_DOWNLOAD_TIMEOUT_MS,
} from '../infrastructure/driven/syncCatalogTimeouts.ts';

export type SyncState = 'CHECKING_NETWORK' | 'CHECKING_UPDATES' | 'READY';

export type StartupSyncOrchestratorOptions = {
  /** When true, allow a longer catalog GET timeout (post snapshot-revision upgrade). */
  isRevisionUpgradePending?: () => boolean;
  /** Called after a successful startup catalog download. */
  onCatalogDownloadSuccess?: () => void;
  downloadTimeoutMs?: number;
  downloadTimeoutAfterRevisionMs?: number;
};

export class StartupSyncOrchestrator {
  private readonly NETWORK_TIMEOUT_MS = 5000;
  private readonly downloadTimeoutMs: number;
  private readonly downloadTimeoutAfterRevisionMs: number;

  constructor(
    private readonly catalogDownload: CatalogDownloadPort,
    private readonly checkNetwork: () => Promise<boolean> = async () => navigator.onLine,
    private readonly options: StartupSyncOrchestratorOptions = {},
  ) {
    this.downloadTimeoutMs = options.downloadTimeoutMs ?? SYNC_CATALOG_STARTUP_DOWNLOAD_TIMEOUT_MS;
    this.downloadTimeoutAfterRevisionMs =
      options.downloadTimeoutAfterRevisionMs ?? SYNC_CATALOG_STARTUP_DOWNLOAD_TIMEOUT_AFTER_REVISION_MS;
  }

  async run(
    onStateChange: (state: SyncState) => void,
    onCatalogProgress?: (progress: CatalogDownloadProgress) => void,
  ): Promise<StartupCatalogSyncOutcome> {
    try {
      onStateChange('CHECKING_NETWORK');

      const isOnline = await this.withTimeout(
        this.checkNetwork(),
        this.NETWORK_TIMEOUT_MS,
        false,
      );

      if (!isOnline) {
        onStateChange('READY');
        return { status: 'offline' };
      }

      onStateChange('CHECKING_UPDATES');

      const pendingUpgrade = this.options.isRevisionUpgradePending?.() ?? false;
      const downloadTimeout = pendingUpgrade ? this.downloadTimeoutAfterRevisionMs : this.downloadTimeoutMs;

      const downloadResult = await this.withTimeout(
        this.catalogDownload.runDownload(onCatalogProgress),
        downloadTimeout,
        null,
      );

      if (downloadResult === null) {
        console.warn(
          `[StartupSync] Catalog download timed out after ${downloadTimeout}ms. Retry from Sync → Descargar ahora.`,
        );
        return { status: 'timeout', timeoutMs: downloadTimeout };
      }

      if (downloadResult.isFailure) {
        console.warn('[StartupSync] Catalog download failed:', downloadResult.error);
        return { status: 'failed', error: downloadResult.error };
      }

      if (downloadResult.value.skippedDuplicateRevision === true) {
        console.info(
          '[StartupSync] Catalog download skipped: server catalog_revision matches localStorage (no DB write).',
        );
        return { status: 'skipped_revision' };
      }

      this.options.onCatalogDownloadSuccess?.();
      return { status: 'applied' };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('[StartupSync] Error during initial validation:', error);
      return { status: 'error', message };
    } finally {
      onStateChange('READY');
    }
  }

  private async withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<T>((resolve) => {
      timeoutId = setTimeout(() => resolve(fallback), ms);
    });

    return Promise.race([
      promise.finally(() => clearTimeout(timeoutId)),
      timeoutPromise,
    ]);
  }
}
